import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatusBadge from './StatusBadge';

/**
 * Reusable Dossier Card Component for mobile view
 * Extracted from mes-dossiers.js to reduce duplication
 */

/**
 * Get display type for a dossier based on formtype
 */
export function getDossierDisplayType(item, includeCompanyType = true) {
  const formType = item.formtype?.toLowerCase() || '';
  let type = '';
  
  if ((item.formtype === 'SAS/SASU' || item.formtype === 'sarl-eurl') && includeCompanyType) {
    try {
      const formData = typeof item?.formdata === 'string' ? JSON.parse(item.formdata) : item?.formdata;
      type = formData?.companyType?.type?.toUpperCase() || '';
    } catch {
      type = formType.toUpperCase();
    }
  } else {
    type = formType.toUpperCase();
  }

  const creationTypes = ['sas', 'sasu', 'srl', 'eurl', 'sci', 'sarl', 'sas/sasu', 'sarl-eurl'];
  
  if (creationTypes.includes(formType)) {
    return includeCompanyType ? `Création de ${type}` : 'Création';
  }
  if (formType === 'formalites' || formType === 'formalities') {
    return includeCompanyType ? `Approbation des comptes - ${type}` : 'Approbation des comptes';
  }
  if (formType === 'dissolution') return 'Dissolution';
  if (formType === 'modification') return 'Modification';
  
  return item.formtype || '-';
}

/**
 * Get legal form type for modification dossiers
 */
export function getLegalFormType(item) {
  const formType = item.formtype?.toLowerCase() || '';
  if (formType !== 'modification') return '';

  try {
    const formData = typeof item?.formdata === 'string' ? JSON.parse(item.formdata) : item?.formdata;
    const legalForm = formData?.company?.legalForm?.toLowerCase() || '';

    if (legalForm.includes('sas') || legalForm.includes('société par actions simplifiée')) {
      return legalForm.includes('unipersonnelle') ? 'SASU' : 'SAS';
    }
    if (legalForm.includes('sarl') || legalForm.includes('société à responsabilité limitée')) {
      return 'SARL';
    }
    if (legalForm.includes('eurl') || legalForm.includes('entreprise unipersonnelle')) {
      return 'EURL';
    }
    if (legalForm.includes('sci') || legalForm.includes('sc') || legalForm.includes('société civile')) {
      return 'SCI';
    }
  } catch {
    return '';
  }
  return '';
}

/**
 * Get action URL for a dossier
 */
export function getDossierActionUrl(item) {
  const formTypeMap = {
    'sarl-eurl': 'creation/sarl-eurl',
    'SAS/SASU': 'creation/sas-sasu',
    'sci': 'creation/sci',
    'modification': 'modification',
    'dissolution': 'dissolution',
    'formalities': 'approbation-des-comptes',
  };

  const path = formTypeMap[item.formtype] || 'creation';
  return `${process.env.REACT_APP_EXPERT_URL}/${path}?formId=${item.id}`;
}

/**
 * Mobile card view for a single dossier
 */
export default function DossierCard({ 
  item, 
  onLinkClick,
}) {
  const displayType = getDossierDisplayType(item, false);

  const renderAction = () => {
    if (item.status === 0) {
      const url = getDossierActionUrl(item);
      return (
        <a
          href="#"
          onClick={(e) => onLinkClick(e, url)}
          className="text-sm sm:text-base font-semibold text-white flex items-center justify-center px-2 sm:px-4 py-2 rounded-md bg-primary w-full"
        >
          Compléter mon dossier
        </a>
      );
    }
    if (item.status === 1) {
      return (
        <a
          href={`/complete-formalities/${item.id}`}
          className="text-sm sm:text-base font-semibold text-white flex items-center justify-center text-center px-2 sm:px-4 py-2 rounded-md bg-primary w-full"
        >
          Compléter le dossier
        </a>
      );
    }
    if (item.status === 2) {
      return (
        <a
          href={`/complete-formalities/${item.id}`}
          className="text-sm sm:text-base font-semibold text-white flex items-center justify-center px-2 sm:px-4 py-2 rounded-md bg-primary w-full"
        >
          En cours de traitement par nos équipes
        </a>
      );
    }
    return <p>Finalisé avec succès</p>;
  };

  return (
    <div className="mt-3">
      <div
        className="flex flex-col justify-center gap-5 items-center bg-white rounded-[20px] shadow-md p-3 mt-4"
        style={{ border: '1px solid #d9d9d9' }}
      >
        <DossierCardRow label="Dossier" value={displayType} />
        <DossierCardRow label="Entreprise" value={item.entreprisename || '-'} />
        <DossierCardRow label="Créé le" value={moment(item.created_at).format('DD/MM/YYYY')} />
        <DossierCardRow label="Statut">
          <StatusBadge status={item.status} type="dossier" fullWidth />
        </DossierCardRow>
        <DossierCardRow label="Action">
          {renderAction()}
        </DossierCardRow>
      </div>
    </div>
  );
}

DossierCard.propTypes = {
  item: PropTypes.object.isRequired,
  onLinkClick: PropTypes.func.isRequired,
};

/**
 * Single row in a dossier card
 */
function DossierCardRow({ label, value, children }) {
  return (
    <div className="flex flex-row justify-start gap-5 items-start w-full" style={{ gap: '15px' }}>
      <div className="font-medium text-gray-500" style={{ minWidth: '100px' }}>
        {label}
      </div>
      <div style={{ width: '100%' }}>
        {children || value}
      </div>
    </div>
  );
}

DossierCardRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  children: PropTypes.node,
};
